import React, { useEffect, useMemo, useState } from 'react';
import { theme } from 'lib/style';
import {
  DocumentHead,
  Dropdown,
  LoadingIndicator,
  PageTemplate,
} from 'lib/components';
import {
  ButtonsBox,
  ButtonsWrapper,
  Container,
  Content,
  DetailsWrapper,
  ErrorMessageText,
  GuideForm,
  GuidesWrapper,
  Header,
  InputWrapper,
  List,
  TextArea,
  TextInput,
  VideoTitle,
  VideoWrapper,
  ErrorMessage as CustomErrorMessage,
} from './styles';
import {
  ErrorMessage,
  Field,
  FieldArray,
  FieldProps,
  Form,
  Formik,
} from 'formik';
import { Gap } from 'lib/components/styles/layout';
import { VideoPlayer } from 'app/pages/video/videoPlayer';
import {
  EditableGuides,
  useEditableGuideCategoriesQuery,
} from 'lib/api/guides/editableGuideCategoriesQuery';
import RouteLeavingGuard from 'app/pages/video/videoDetails/main/RouteLeavingGuard';
import { useHistory, useLocation } from 'react-router';
import * as Yup from 'yup';
import { GuideUploadProps, useCreateGuides } from 'lib/api/guides/createGuides';
import { Button } from 'react-covideo-common';
import { Fragment } from 'react';
import { CustomizedState, IOption } from './constants';
import { VideoData } from 'lib/hooks';
import { getVideo } from 'lib/api/videos/useSingleVideoQuery';
import {
  FieldWrapper,
  LinkItem,
  LinksHeader,
  Input as ExtendedInput,
  Label,
} from 'app/pages/design/callsToAction/InteractiveCTAs';
import { ParagraphSmallBold } from 'lib/components/styles/typography';
import { wrapWithHttp } from 'lib/utils/functions';
import { URL_REGEX } from 'lib/utils/regexes';
import { MdDeleteForever, MdOpenInNew, MdSave } from 'react-icons/md';
import { IoMdLink } from 'react-icons/io';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

export const makeReviewUploadGuidesSchema = ({
  t,
  urlRegex,
  maxLen = 1000,
}: {
  t: TFunction;
  urlRegex: RegExp;
  maxLen?: number;
}) =>
  Yup.object({
    guides: Yup.array().of(
      Yup.object({
        name: Yup.string().required(t('Required')),
        guideCategoryId: Yup.number().nullable().required(t('Required')),
        videoId: Yup.string().required(t('Required')),
        description: Yup.string()
          .max(maxLen, t('Description has a maximum limit of 1000 characters.'))
          .nullable(),
        links: Yup.array()
          .of(
            Yup.object({
              label: Yup.string().required(t('Display name is required')),
              link: Yup.string()
                .required(t('URL is required'))
                .matches(urlRegex, t('Invalid URL')),
            })
          )
          .nullable(true),
      })
    ),
  });

export const ReviewUploadedGuides = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const state = location.state as CustomizedState;
  const history = useHistory();
  const { mutateAsync: createGuides, isLoading: isCreatingGuides } =
    useCreateGuides();
  const { data, isLoading: isLoadingCategories } =
    useEditableGuideCategoriesQuery();
  const [videoArray, setVideoArray] = useState<VideoData[]>([]);
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownOptions = (data || []).map((category: EditableGuides) => ({
    value: category.guideCategoryId,
    label: category.name,
  }));

  // in component
  const reviewUploadGudesValidationSchema = useMemo(
    () => makeReviewUploadGuidesSchema({ t, urlRegex: new RegExp(URL_REGEX) }),
    [i18n.language]
  );

  useEffect(() => {
    if (state && state.videos && state.videos.length) {
      const videoIds = state.videos;
      setIsLoading(true);
      Promise.all(
        videoIds.map((id: string) => {
          return getVideo(id);
        })
      )
        .then(results => {
          setVideoArray(results);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    } else {
      setIsSaved(true);
    }
  }, []);

  //Initial Guide values
  const initialValues: GuideUploadProps[] = videoArray.map(
    (video: VideoData) => ({
      guideCategoryId: state?.category ?? null,
      name: video.title,
      videoId: video.id,
      description: '',
      videoSource: video.videoSource,
      links: [],
      playerBackgroundColor: video.playerBackgroundColor,
      playButtonPosition: video.playButtonPosition,
      playerIconsAndTextColor: video.playerIconsAndTextColor,
    })
  );

  const handleCreateGuides = async (payload: GuideUploadProps[]) => {
    const formattedPayload = payload.map(guide => ({
      ...guide,
      links: guide.links?.map(link => ({
        ...link,
        link: wrapWithHttp(link.link),
      })),
    }));
    await createGuides(formattedPayload);
    setIsSaved(true);
  };

  useEffect(() => {
    if (isSaved) {
      history.push(`/guides/${state.category}`);
    }
  }, [isSaved]);

  const loading = isLoading || isLoadingCategories;

  if (loading) {
    return <LoadingIndicator isLoading={loading} text={t('Loading...')} />;
  }

  const ReviewContent = () => {
    const { t } = useTranslation();

    return (
      <Content>
        <Container>
          <List>
            <div>
              <Formik
                initialValues={{ guides: initialValues }}
                validationSchema={reviewUploadGudesValidationSchema}
                enableReinitialize
                onSubmit={values => handleCreateGuides(values.guides)}
                render={({ values, setFieldValue, errors }) => (
                  <Form>
                    <FieldArray
                      name='guides'
                      render={arrayHelpers => (
                        <GuidesWrapper>
                          <ButtonsBox>
                            <ButtonsWrapper>
                              <Button
                                type='submit'
                                icon={<MdSave size={18} />}
                                text={
                                  videoArray.length > 1
                                    ? t('Save Training Videos')
                                    : t('Save Training Video')
                                }
                                disabled={
                                  isCreatingGuides ||
                                  Object.keys(errors).length !== 0
                                }
                              />
                            </ButtonsWrapper>
                          </ButtonsBox>
                          {values.guides &&
                            values.guides.length > 0 &&
                            values.guides.map(
                              (guide: GuideUploadProps, index: number) => {
                                const hasLinks =
                                  !!values?.guides[index].links?.length;
                                const links = values?.guides[index].links;
                                const description =
                                  values?.guides[index].description;
                                return (
                                  <Fragment key={guide.videoId}>
                                    <VideoTitle>{guide.name}</VideoTitle>
                                    <GuideForm>
                                      <Gap
                                        alignItems='self-start'
                                        flexDirection='column'
                                        width='100%'
                                      >
                                        <InputWrapper>
                                          <Label>
                                            {t('Training Video Title')}
                                          </Label>
                                          <Field
                                            name={`guides.${index}.name`}
                                            as={TextInput}
                                          />
                                          <ErrorMessage
                                            name={`guides.${index}.name`}
                                            component={ErrorMessageText}
                                          />
                                        </InputWrapper>
                                        <Button
                                          onClick={() => {
                                            if (
                                              values.guides.length - 1 ===
                                              0
                                            ) {
                                              history.push('/home');
                                              return;
                                            }
                                            arrayHelpers.remove(index);
                                            setVideoArray(prevVideos =>
                                              prevVideos.filter(
                                                video =>
                                                  video.id !== guide.videoId
                                              )
                                            );
                                          }}
                                          text={t('Remove this video')}
                                          variant='destructive'
                                          icon={
                                            <MdDeleteForever
                                              size={18}
                                              color={theme.palette.buttonDelete}
                                            />
                                          }
                                        />
                                        <DetailsWrapper>
                                          <Header marginBottom='16px'>
                                            {t('Details')}
                                          </Header>
                                          <Gap>
                                            <InputWrapper>
                                              <Label>{t('Description')}</Label>
                                              <Field
                                                name={`guides.${index}.description`}
                                                as={TextArea}
                                                maxRowLength={54}
                                                textLength={
                                                  description?.length || 0
                                                }
                                              />
                                              <ErrorMessage
                                                name={`guides.${index}.description`}
                                                component={ErrorMessageText}
                                              />
                                            </InputWrapper>
                                            <InputWrapper>
                                              <Label>{t('Category')}</Label>
                                              <Dropdown
                                                options={dropdownOptions}
                                                value={
                                                  dropdownOptions.find(
                                                    item =>
                                                      item.value ===
                                                      values.guides[index]
                                                        .guideCategoryId
                                                  ) || null
                                                }
                                                onChange={(item: IOption) => {
                                                  setFieldValue(
                                                    `guides.${index}.guideCategoryId`,
                                                    item.value
                                                  );
                                                }}
                                                creatable={false}
                                                extendStyles={{
                                                  container: {
                                                    width: '100%',
                                                    margin: '0px 0 0px 0',
                                                  },
                                                }}
                                                isSearchable={false}
                                                placeholder={t(
                                                  'Select Category'
                                                )}
                                                menuPortalTarget={document.body}
                                                menuShouldBlockScroll={true}
                                                menuPosition='absolute'
                                                menuPlacement='bottom'
                                              />
                                              <ErrorMessage
                                                name={`guides.${index}.guideCategoryId`}
                                                component={ErrorMessageText}
                                              />
                                            </InputWrapper>
                                            <Gap
                                              flexDirection='column'
                                              alignItems='stretch'
                                              gap='22px'
                                              m='32px 0 0 0'
                                            >
                                              <Header
                                                style={{ height: '12px' }}
                                              >
                                                {t('Assigned Articles & Links')}
                                              </Header>
                                              <InputWrapper>
                                                {hasLinks && (
                                                  <LinksHeader>
                                                    <Label width='178px'>
                                                      {t('Display Name')}
                                                    </Label>
                                                    <Label width='198px'>
                                                      {t('URL')}
                                                    </Label>
                                                    <Label width='84px'>
                                                      {t('Preview')}
                                                    </Label>
                                                  </LinksHeader>
                                                )}
                                                <FieldArray
                                                  name={`guides[${index}].links`}
                                                  render={arrHelpers => (
                                                    <>
                                                      {links?.map(
                                                        (
                                                          ctaValue,
                                                          idx: number
                                                        ) => (
                                                          <LinkItem
                                                            key={idx}
                                                            height='auto'
                                                          >
                                                            <FieldWrapper
                                                              width={`178px`}
                                                            >
                                                              <Field
                                                                name={`guides[${index}].links[${idx}].label`}
                                                              >
                                                                {({
                                                                  field,
                                                                  meta,
                                                                }: FieldProps) => (
                                                                  <>
                                                                    <ExtendedInput
                                                                      {...field}
                                                                      type='text'
                                                                      placeholder={t(
                                                                        'Display Name'
                                                                      )}
                                                                      maxLength={
                                                                        35
                                                                      }
                                                                      hasError={
                                                                        meta.touched &&
                                                                        !!meta.error
                                                                      }
                                                                    />
                                                                    {meta.touched &&
                                                                      meta.error && (
                                                                        <CustomErrorMessage>
                                                                          {
                                                                            meta.error
                                                                          }
                                                                        </CustomErrorMessage>
                                                                      )}
                                                                  </>
                                                                )}
                                                              </Field>
                                                            </FieldWrapper>
                                                            <FieldWrapper
                                                              width={`186px`}
                                                            >
                                                              <Field
                                                                name={`guides[${index}].links[${idx}].link`}
                                                              >
                                                                {({
                                                                  field,
                                                                  meta,
                                                                }: FieldProps) => (
                                                                  <>
                                                                    <ExtendedInput
                                                                      {...field}
                                                                      type='text'
                                                                      placeholder={t(
                                                                        'URL'
                                                                      )}
                                                                      maxLength={
                                                                        35
                                                                      }
                                                                      hasError={
                                                                        meta.touched &&
                                                                        !!meta.error
                                                                      }
                                                                    />
                                                                    {meta.touched &&
                                                                      meta.error && (
                                                                        <CustomErrorMessage>
                                                                          {
                                                                            meta.error
                                                                          }
                                                                        </CustomErrorMessage>
                                                                      )}
                                                                  </>
                                                                )}
                                                              </Field>
                                                            </FieldWrapper>
                                                            <FieldWrapper width='46px'>
                                                              <Button
                                                                variant='secondary'
                                                                text=''
                                                                icon={
                                                                  <MdOpenInNew size='18px' />
                                                                }
                                                                onClick={() => {
                                                                  window.open(
                                                                    ctaValue.link,
                                                                    '_blank'
                                                                  );
                                                                }}
                                                                disabled={
                                                                  !ctaValue.link
                                                                }
                                                              />
                                                            </FieldWrapper>

                                                            <FieldWrapper width='46px'>
                                                              <Button
                                                                variant='destructive'
                                                                text=''
                                                                icon={
                                                                  <MdDeleteForever
                                                                    size='24px'
                                                                    color={
                                                                      theme
                                                                        .palette
                                                                        .buttonDelete
                                                                    }
                                                                  />
                                                                }
                                                                onClick={(
                                                                  e?:
                                                                    | React.SyntheticEvent
                                                                    | React.SyntheticEvent<
                                                                        Element,
                                                                        Event
                                                                      >
                                                                    | React.MouseEvent
                                                                ) => {
                                                                  e?.preventDefault();
                                                                  arrHelpers.remove(
                                                                    idx
                                                                  );
                                                                }}
                                                              />
                                                            </FieldWrapper>
                                                          </LinkItem>
                                                        )
                                                      )}
                                                      <Button
                                                        onClick={() => {
                                                          arrHelpers.push({
                                                            link: '',
                                                            label: '',
                                                          });
                                                        }}
                                                        text={
                                                          links.length === 0
                                                            ? t('New Link')
                                                            : t('Add Links...')
                                                        }
                                                        variant='secondary'
                                                        icon={
                                                          <IoMdLink
                                                            color={
                                                              theme.palette
                                                                .covideoBlue100
                                                            }
                                                            size={'20px'}
                                                          />
                                                        }
                                                        disabled={
                                                          links
                                                            ? links?.length >=
                                                              10
                                                            : false
                                                        }
                                                      />
                                                      {links?.length >= 10 && (
                                                        <ParagraphSmallBold
                                                          style={{
                                                            color:
                                                              theme.palette
                                                                .red100,
                                                          }}
                                                        >
                                                          {t(
                                                            'This training video can’t have any more assigned articles and links.'
                                                          )}
                                                        </ParagraphSmallBold>
                                                      )}
                                                    </>
                                                  )}
                                                />
                                              </InputWrapper>
                                            </Gap>
                                          </Gap>
                                        </DetailsWrapper>
                                      </Gap>
                                      <VideoWrapper>
                                        <VideoPlayer
                                          playerBackgroundColor={
                                            guide.playerBackgroundColor
                                          }
                                          playButtonPosition={
                                            guide.playButtonPosition
                                          }
                                          playerIconsColor={
                                            guide.playerIconsAndTextColor
                                          }
                                          videoSource={guide.videoSource}
                                          videoRef={React.createRef()}
                                          videoId={guide.videoId.toString()}
                                        />
                                      </VideoWrapper>
                                    </GuideForm>
                                  </Fragment>
                                );
                              }
                            )}
                        </GuidesWrapper>
                      )}
                    />
                  </Form>
                )}
              />
            </div>
          </List>
        </Container>
      </Content>
    );
  };

  return (
    <>
      <DocumentHead title={t('Review Training Videos')} />
      <PageTemplate
        main={
          <>
            <RouteLeavingGuard
              when={true}
              stay={true}
              onConfirm={() => {}}
              navigate={path => history.push(path)}
              shouldBlockNavigation={() => {
                return !isSaved;
              }}
              title={t('Leave without saving details?')}
              text={t(
                "Your changes will not be saved. This action can't be undone."
              )}
              confirmButtonText={t('Continue')}
              discardButtonText={t('Leave')}
            />
            <ReviewContent />
          </>
        }
      />
    </>
  );
};
